import useStyles from 'isomorphic-style-loader/useStyles';

import type { ModalType } from '@models';
import Modal, { useModal } from '@components/modal';

import { CommandPaletteSurface } from './command-palette-surface';
import type {
  CommandPaletteOpenArgs,
  CommandPaletteScopeDefinition,
} from './command-palette.models';
// eslint-disable-next-line postcss-modules/no-unused-class -- the shell and surface share this stylesheet
import s from './command-palette.scss';

const LISTBOX_ID = 'commandPaletteResults';

export interface CommandPaletteProps {
  /**
   * The modal's accessible name, e.g. "Quick Search".
   */
  label: string;
  /**
   * The modal-registry key this palette opens under.
   */
  modalId: ModalType;
  /**
   * The scopes this palette can host; the first is the default.
   */
  scopes: CommandPaletteScopeDefinition[];
}

/**
 * A scope-agnostic command palette. It owns the modal and picks the active
 * scope from the open args, rendering that scope's surface. The surface is
 * keyed by scope so switching (or reopening) starts it fresh.
 */
export const CommandPalette = ({
  label,
  modalId,
  scopes,
}: CommandPaletteProps) => {
  useStyles(s);
  const { close, data, isOpen } = useModal(modalId);

  // The palette's modal payload is CommandPaletteOpenArgs; the consumer's
  // registry entry enforces it (modalId is the generic ModalType union here).
  const openArgs = data as CommandPaletteOpenArgs | undefined;

  const activeScope =
    scopes.find(scope => scope.key === openArgs?.scope) ?? scopes[0];

  return (
    <Modal aria-label={label} className={s.root} closeIcon={false} id={modalId}>
      <CommandPaletteSurface
        key={activeScope.key}
        close={close}
        initialQuery={openArgs?.query ?? ''}
        isOpen={isOpen}
        listboxId={LISTBOX_ID}
        scope={activeScope}
      />
    </Modal>
  );
};

export default CommandPalette;
